export const CUSTOMER_PORTAL_SESSION_KEY = "customerPortalSession";

/** @deprecated legacy flag; cleared on logout */
export const CUSTOMER_PORTAL_AUTH_KEY = "customerPortalAuthenticated";

function readSessionRaw() {
  // Prefer localStorage so email/SMS links in a new tab keep an existing portal session.
  // Fall back to legacy sessionStorage and migrate once found.
  try {
    const fromLocal = localStorage.getItem(CUSTOMER_PORTAL_SESSION_KEY);
    if (fromLocal) return fromLocal;
  } catch {
    /* ignore */
  }
  try {
    const fromSession = sessionStorage.getItem(CUSTOMER_PORTAL_SESSION_KEY);
    if (fromSession) {
      try {
        localStorage.setItem(CUSTOMER_PORTAL_SESSION_KEY, fromSession);
        sessionStorage.removeItem(CUSTOMER_PORTAL_SESSION_KEY);
      } catch {
        /* ignore migrate failures */
      }
      return fromSession;
    }
  } catch {
    /* ignore */
  }
  return null;
}

function writeSessionRaw(value) {
  try {
    localStorage.setItem(CUSTOMER_PORTAL_SESSION_KEY, value);
  } catch {
    /* ignore */
  }
  try {
    sessionStorage.removeItem(CUSTOMER_PORTAL_SESSION_KEY);
  } catch {
    /* ignore */
  }
}

export function initialsFromName(name) {
  if (!name || typeof name !== "string") return "?";
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

export function getCustomerPortalSession() {
  try {
    const raw = readSessionRaw();
    if (!raw) return null;
    const data = JSON.parse(raw);
    if (!data?.token || Number(data?.user?.role_id) !== 6) return null;
    return data;
  } catch {
    return null;
  }
}

export function setCustomerPortalSession(apiResponse) {
  const nameParts = (apiResponse.full_name || "").trim().split(/\s+/).filter(Boolean);
  const user = {
    id: apiResponse.id,
    full_name: apiResponse.full_name,
    first_name: apiResponse.first_name || nameParts[0] || "",
    last_name: apiResponse.last_name || nameParts.slice(1).join(" ") || "",
    email: apiResponse.email,
    phone_number: apiResponse.phone_number,
    country_code: apiResponse.country_code,
    role_id: apiResponse.role_id,
    salon_id: apiResponse.salon_id,
    profile_photo_url: apiResponse.profile_photo_url,
    customer_portal_settings: apiResponse.customer_portal_settings ?? null,
    cancellation_policy: apiResponse.cancellation_policy ?? null,
  };
  writeSessionRaw(
    JSON.stringify({
      token: apiResponse.token,
      auth_key: apiResponse.auth_key,
      user,
    }),
  );
}

export function updateCustomerPortalUserProfile(profile) {
  const session = getCustomerPortalSession();
  if (!session?.token) return;

  setCustomerPortalSession({
    token: session.token,
    auth_key: session.auth_key,
    id: profile.id ?? session.user.id,
    full_name: profile.full_name ?? session.user.full_name,
    first_name: profile.first_name ?? session.user.first_name,
    last_name: profile.last_name ?? session.user.last_name,
    email: profile.email ?? session.user.email,
    phone_number: profile.phone_number ?? session.user.phone_number,
    country_code: profile.country_code ?? session.user.country_code,
    role_id: profile.role_id ?? session.user.role_id,
    salon_id: profile.salon_id ?? session.user.salon_id,
    profile_photo_url: profile.profile_photo_url ?? session.user.profile_photo_url,
    customer_portal_settings: profile.customer_portal_settings ?? session.user.customer_portal_settings ?? null,
    cancellation_policy: profile.cancellation_policy ?? session.user.cancellation_policy ?? null,
  });
}

export function updateCustomerPortalSettingsInSession(settings) {
  const session = getCustomerPortalSession();
  if (!session?.token) return;

  writeSessionRaw(
    JSON.stringify({
      ...session,
      user: {
        ...session.user,
        customer_portal_settings: settings,
      },
    }),
  );
}

export function clearCustomerPortalSession() {
  try {
    localStorage.removeItem(CUSTOMER_PORTAL_SESSION_KEY);
    localStorage.removeItem(CUSTOMER_PORTAL_AUTH_KEY);
  } catch {
    /* ignore */
  }
  try {
    sessionStorage.removeItem(CUSTOMER_PORTAL_SESSION_KEY);
    sessionStorage.removeItem(CUSTOMER_PORTAL_AUTH_KEY);
  } catch {
    /* ignore */
  }
}

/** Best post-login/home path from react-router location.state.from or a fallback. */
export function resolveCustomerPortalReturnPath(from, fallback = "/customer-portal/dashboard") {
  if (!from) return fallback;
  if (typeof from === "string") {
    return from.startsWith("/customer-portal") ? from : fallback;
  }
  const pathname = typeof from.pathname === "string" ? from.pathname : "";
  if (!pathname.startsWith("/customer-portal") || pathname === "/customer-portal/login") {
    return fallback;
  }
  const search = typeof from.search === "string" ? from.search : "";
  const hash = typeof from.hash === "string" ? from.hash : "";
  return `${pathname}${search}${hash}`;
}
